import { gqlPromise, type DefaultContext } from '@egw/clients'
import {
  type InvestmentTradeDetailInput,
  type InvestmentTradeDetailOutput,
  type InvestmentTradeConfirmsInput,
  type InvestmentTradeConfirmsOutput,
} from '../../../models'

/**
 * Get Investment Account Detail
 * @param investmentTradeDetailInput - tradeId: number
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<InvestmentTradeDetailOutput>
 * @example const investmentTradeDetail = await getInvestmentTradeDetail(\{ tradeId: number \});
 */
export const getInvestmentTradeDetail = async (
  investmentTradeDetailInput: InvestmentTradeDetailInput,
  context?: DefaultContext,
): Promise<InvestmentTradeDetailOutput> => {
  const response = (await gqlPromise(
    {
      name: 'investmentTradeDetail',
      query: `query investmentTradeDetail($investmentTradeDetailInput: InvestmentTradeDetailInput!) {
        investmentTradeDetail(investmentTradeDetailInput: $investmentTradeDetailInput) {
            ... on InvestmentTradeDetailError {
                __typename
                errorType
            }
            ... on InvestmentTradeDetail {
                __typename
                tradeId
                displayName
                amountCash {
                amount
                currencyCode
                }
                quantity
                status
                executedTime
                instrument {
                name
                description
                symbol
                priorClosePrice {
                    amount
                    currencyCode
                }
                image
                ... on EquitiesInstrument {
                    sector
                    __typename
                }
                ... on MutualFundInstrument {
                    __typename
                }
                ... on ETFInstrument {
                    __typename
                }
                }
            }
        }
    }`,
    },
    { investmentTradeDetailInput },
    context,
  )) as { investmentTradeDetail: InvestmentTradeDetailOutput }
  return response.investmentTradeDetail
}

/**
 * Get investment trade confirms
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<InvestmentTradeConfirms>
 * @example const customerDetails = await getInvestmentTradeConfirms();
 */
export const getInvestmentTradeConfirms = async (
  investmentTradeConfirmsInput: InvestmentTradeConfirmsInput,
  context?: DefaultContext,
): Promise<InvestmentTradeConfirmsOutput> => {
  const response = (await gqlPromise(
    {
      name: 'investmentTradeConfirms',
      query: `
        query investmentTradeConfirms($investmentTradeConfirmsInput: InvestmentTradeConfirmsInput!) {
          investmentTradeConfirms(investmentTradeConfirmsInput: $investmentTradeConfirmsInput) {
            confirms {
              displayName
              fileKey
              alias
              url
            }
          }
        }`,
    },
    { investmentTradeConfirmsInput },
    context,
  )) as { investmentTradeConfirms: InvestmentTradeConfirmsOutput }
  return response.investmentTradeConfirms
}
